/**
 * HillSelect Component
 * Provides typeahead/dropdown for selecting a hill
 * Fetches hills from API with search functionality
 */

import { useState, useEffect } from 'react';
import Select from 'react-select';

function HillSelect({ selectedHill, onSelect, disabled }) {
  const [hills, setHills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch hills from API
   * Called on component mount and when search term changes
   */
  const fetchHills = async (searchTerm = '') => {
    setLoading(true);
    setError(null);

    try {
      const url = searchTerm
        ? `/api/hills?search=${encodeURIComponent(searchTerm)}`
        : '/api/hills';

      const response = await fetch(url, {
        headers: {
          'X-API-Key': import.meta.env.VITE_API_KEY || ''
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch hills');
      }

      const data = await response.json();
      setHills(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching hills:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load initial hills on mount
  useEffect(() => {
    fetchHills();
  }, []);

  /**
   * Handle search input change
   * Debounced search to avoid excessive API calls
   */
  const handleInputChange = (inputValue) => {
    if (inputValue.length >= 2 || inputValue.length === 0) {
      fetchHills(inputValue);
    }
  };

  /**
   * Transform hill data for react-select
   */
  const hillOptions = hills.map(hill => ({
    value: hill,
    label: hill
  }));

  const selectedOption = selectedHill
    ? { value: selectedHill, label: selectedHill }
    : null;

  return (
    <div className="hill-select">
      <h2>Select Hill</h2>
      <p className="step-description">
        Choose the hillclimb venue to analyze
      </p>

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      <Select
        options={hillOptions}
        value={selectedOption}
        onChange={(option) => onSelect(option ? option.value : null)}
        onInputChange={handleInputChange}
        isLoading={loading}
        isDisabled={disabled}
        isClearable
        placeholder="Select or search for a hill..."
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2
            ? 'Type at least 2 characters to search'
            : 'No hills found'
        }
        className="hill-select-input"
        classNamePrefix="select"
        styles={{
          control: (base) => ({
            ...base,
            minHeight: '50px',
            fontSize: '16px'
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999
          })
        }}
      />

      {hills.length > 0 && !selectedHill && (
        <p className="hint-text">
          💡 Showing {hills.length} hill{hills.length !== 1 ? 's' : ''}. Type to narrow down results.
        </p>
      )}
    </div>
  );
}

export default HillSelect;
